import type { Prisma } from "@gt/prisma";

export type UserWithCompany = Prisma.UserGetPayload<{ include: { company: true } }>;

/** Company has passed external/system verification — full app routes allowed. */
export const CLIENT_ONBOARDING_COMPLETE_STATUSES = new Set(["SYSTEM_VERIFIED", "VERIFIED"]);

/** Statuses that mean the user has finished profile builder at least once (not signup-only company). */
export const CLIENT_PROFILE_CREATED_STATUSES = new Set([
  "UNDER_REVIEW",
  "SYSTEM_VERIFIED",
  "VERIFIED",
  "ACTION_REQUIRED",
]);

export function isClientOnboardingComplete(user: UserWithCompany | null | undefined): boolean {
  const status = user?.company?.status;
  return Boolean(user?.company && status && CLIENT_ONBOARDING_COMPLETE_STATUSES.has(status));
}

/**
 * True once the client has saved a real company profile (not just signup shell).
 * Derived only from API user/company payload — no localStorage.
 */
export function hasClientCreatedProfile(user: UserWithCompany | null | undefined): boolean {
  const company = user?.company;
  if (!company) return false;

  if (company.website?.trim()) return true;
  if (company.description?.trim()) return true;
  if (company.status && CLIENT_PROFILE_CREATED_STATUSES.has(company.status)) return true;

  return false;
}

/** Where to send a CLIENT after auth when using default app shell (not admin). */
export function getDefaultClientPathAfterAuth(
  user: UserWithCompany | null | undefined,
): "/my-discovery" | "/create-profile" {
  // API failure / unknown user: never assume "new user" — avoid trapping existing clients on create-profile.
  if (!user) return "/my-discovery";
  if (hasClientCreatedProfile(user)) return "/my-discovery";
  return "/create-profile";
}

/** Post-login destination from portal user role (clients, facilitators, admins). */
export function getDefaultPathAfterAuth(user: UserWithCompany | null | undefined): string {
  if (!user) return "/my-discovery";
  if (user.role === "FACILITATOR") return "/facilitator/dashboard";
  if (user.role === "ECOSYSTEM_HEAD") return "/ecosystem-head/dashboard";
  if (user.role === "ADMIN" || user.role === "SUB_ADMIN") return "/admin/companies";
  return getDefaultClientPathAfterAuth(user);
}
